import React from 'react';
import { ActivityIndicator, Pressable, View, type ViewStyle } from 'react-native';
import { Icon, type IconName } from './Icon';
import { Text } from './Text';
import { radius, useTheme, type Colors } from './theme';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

export interface ButtonProps {
  title?: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  iconRight?: IconName;
  loading?: boolean;
  disabled?: boolean;
  block?: boolean;
  style?: ViewStyle;
  accessibilityLabel?: string;
  testID?: string;
}

const palette = (c: Colors, v: ButtonVariant, pressed: boolean) => {
  switch (v) {
    case 'primary':
      return { bg: pressed ? c.primaryHover : c.primary, fg: c.onPrimary, border: 'transparent' };
    case 'danger':
      return { bg: pressed ? c.dangerSoft : c.danger, fg: pressed ? c.danger : '#fff', border: 'transparent' };
    case 'success':
      return { bg: c.success, fg: '#fff', border: 'transparent' };
    case 'ghost':
      return { bg: pressed ? c.hover : 'transparent', fg: c.text, border: 'transparent' };
    default:
      return { bg: pressed ? c.hover : c.surface, fg: c.text, border: c.borderStrong };
  }
};

const HEIGHT = { sm: 30, md: 38, lg: 46 };

export function Button({ title, onPress, variant = 'secondary', size = 'md', icon, iconRight, loading, disabled, block, style, accessibilityLabel, testID }: ButtonProps) {
  const { colors } = useTheme();
  const off = disabled || loading;
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? title}
      accessibilityState={{ disabled: !!off, busy: !!loading }}
      disabled={off}
      onPress={onPress}
      style={({ pressed }) => {
        const p = palette(colors, variant, pressed);
        return [
          {
            height: HEIGHT[size],
            paddingHorizontal: title ? (size === 'sm' ? 10 : 14) : 0,
            width: title ? undefined : HEIGHT[size],
            borderRadius: radius.md,
            backgroundColor: p.bg,
            borderWidth: 1,
            borderColor: p.border,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            opacity: off ? 0.55 : 1,
            alignSelf: block ? 'stretch' : 'flex-start',
          } as ViewStyle,
          style,
        ];
      }}
    >
      {({ pressed }) => {
        const p = palette(colors, variant, pressed);
        return (
          <>
            {loading ? <ActivityIndicator size="small" color={p.fg} /> : icon ? <Icon name={icon} size={size === 'sm' ? 14 : 16} color={p.fg} /> : null}
            {!!title && (
              <Text weight="medium" color={p.fg} size={size === 'sm' ? 12 : size === 'lg' ? 15 : 13} numberOfLines={1}>
                {title}
              </Text>
            )}
            {iconRight && <Icon name={iconRight} size={14} color={p.fg} />}
          </>
        );
      }}
    </Pressable>
  );
}

/** Square icon-only button (accessibilityLabel required). */
export function IconButton({ icon, onPress, label, badge, variant = 'ghost', size = 'md', testID }: { icon: IconName; onPress?: () => void; label: string; badge?: number; variant?: ButtonVariant; size?: 'sm' | 'md' | 'lg'; testID?: string }) {
  const { colors } = useTheme();
  return (
    <View>
      <Button icon={icon} onPress={onPress} accessibilityLabel={label} variant={variant} size={size} testID={testID} />
      {!!badge && (
        <View style={{ pointerEvents: 'none', position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, borderRadius: 8, paddingHorizontal: 4, backgroundColor: colors.danger, alignItems: 'center', justifyContent: 'center' }}>
          <Text size={10} weight="bold" color="#fff">
            {badge > 99 ? '99+' : badge}
          </Text>
        </View>
      )}
    </View>
  );
}
